import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import {
  fetchHistoryAbsence,
  fetchHistoryAbsences,
  fetchHistoryTimesheet,
  fetchHistoryTimesheets,
  type Absence,
  type AbsenceHistoryItem,
  type HistoryQuery,
  type Me,
  type TimesheetHistoryItem,
  type TimesheetWeekDetail,
} from "../api/client";
import { StatusBadge, type TimesheetStatus } from "./StatusBadge";
import { formatApprovedBy, formatPendingWith } from "./approverMeta";
import { formatHours } from "../utils/format";

dayjs.locale("ru");

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function currentQuarterRange(today: Date = new Date()): [Date, Date] {
  const year = today.getFullYear();
  const quarter = Math.floor(today.getMonth() / 3); // 0..3
  const start = new Date(year, quarter * 3, 1);
  const end = new Date(year, quarter * 3 + 3, 0);
  return [start, end];
}

function toIso(d: Date | null): string | null {
  if (!d) return null;
  return dayjs(d).format("YYYY-MM-DD");
}

function showsEmployeeFilter(me: Me): boolean {
  if (me.is_staff || me.is_superuser) return true;
  if (me.is_line_manager || me.is_hr || me.is_project_manager) return true;
  const roles = me.roles ?? [];
  return roles.some((r) => ["line_manager", "project_manager", "hr", "admin"].includes(r));
}

// ---------------------------------------------------------------------------
// HistoryView
// ---------------------------------------------------------------------------

export interface HistoryViewProps {
  me: Me;
}

export function HistoryView({ me }: HistoryViewProps) {
  const [defaultFrom, defaultTo] = useMemo(() => currentQuarterRange(), []);
  const [dateFrom, setDateFrom] = useState<Date | null>(defaultFrom);
  const [dateTo, setDateTo] = useState<Date | null>(defaultTo);

  // US-100: «прыжок к человеку» из ⌘K приходит как /history?employee=<id>&name=<ФИО>.
  // Сидируем фильтр из URL; name прокидываем как option, чтобы выбранный
  // сотрудник отображался даже если у него нет строк в текущем периоде.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlEmployee = searchParams.get("employee");
  const urlName = searchParams.get("name");
  const [employeeId, setEmployeeId] = useState<string | null>(urlEmployee);
  const [injectedOption, setInjectedOption] = useState<{ value: string; label: string } | null>(
    urlEmployee && urlName ? { value: urlEmployee, label: urlName } : null,
  );
  // Если палитра ведёт на нового человека, когда HistoryView уже смонтирован,
  // useState-инициализатор не сработает — синхронизируем через effect.
  const appliedEmployeeRef = useRef<string | null>(urlEmployee);
  useEffect(() => {
    if (urlEmployee && urlEmployee !== appliedEmployeeRef.current) {
      appliedEmployeeRef.current = urlEmployee;
      setEmployeeId(urlEmployee);
      if (urlName) setInjectedOption({ value: urlEmployee, label: urlName });
    }
  }, [urlEmployee, urlName]);

  // Ручная смена фильтра отвязывает от deep-link (URL не навязывает выбор назад).
  const handleEmployeeChange = useCallback(
    (v: string | null) => {
      setEmployeeId(v);
      if (searchParams.has("employee")) {
        const next = new URLSearchParams(searchParams);
        next.delete("employee");
        next.delete("name");
        appliedEmployeeRef.current = null;
        setSearchParams(next, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  // US-103.3/103.4: внешний согласующий (timesheet_exempt) таймшиты не ведёт —
  // не показываем ему вкладку «Таймшиты», только «Отсутствия».
  const showTimesheets = !me.timesheet_exempt;
  const [activeTab, setActiveTab] = useState<string | null>(
    showTimesheets ? "timesheets" : "absences",
  );

  const showEmployeeFilter = showsEmployeeFilter(me);

  const query: HistoryQuery | null = useMemo(() => {
    const from = toIso(dateFrom);
    const to = toIso(dateTo);
    if (!from || !to) return null;
    return {
      date_from: from,
      date_to: to,
      ...(employeeId ? { employee_id: Number(employeeId) } : {}),
    };
  }, [dateFrom, dateTo, employeeId]);

  return (
    <Stack gap="md">
      <Title order={3}>История</Title>
      <Group align="flex-end" gap="sm">
        <DatePickerInput
          label="С"
          value={dateFrom}
          onChange={setDateFrom as (v: Date | null) => void}
          valueFormat="DD.MM.YYYY"
          w={160}
        />
        <DatePickerInput
          label="По"
          value={dateTo}
          onChange={setDateTo as (v: Date | null) => void}
          valueFormat="DD.MM.YYYY"
          w={160}
        />
        {showEmployeeFilter && (
          <EmployeeFilter
            value={employeeId}
            onChange={handleEmployeeChange}
            query={query}
            injectedOption={injectedOption}
          />
        )}
      </Group>
      <Text size="xs" c="dimmed">
        Отображается ваша зона ответственности: свои недели/абсансы, подчинённые и проекты (для PM).
        Максимум 100 записей на страницу.
      </Text>

      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          {showTimesheets && <Tabs.Tab value="timesheets">Таймшиты</Tabs.Tab>}
          <Tabs.Tab value="absences">Отсутствия</Tabs.Tab>
        </Tabs.List>
        {showTimesheets && (
          <Tabs.Panel value="timesheets" pt="md">
            <TimesheetHistoryTab query={query} />
          </Tabs.Panel>
        )}
        <Tabs.Panel value="absences" pt="md">
          <AbsenceHistoryTab query={query} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Employee filter — dropdown, построенный из набора сотрудников текущих
// результатов (без отдельного /api/employees/ endpoint'а в M5.1).
// ---------------------------------------------------------------------------

interface EmployeeFilterProps {
  value: string | null;
  onChange: (v: string | null) => void;
  query: HistoryQuery | null;
  // US-100: опция для предвыбранного из ⌘K сотрудника, которого может не быть
  // в списке текущего периода — чтобы Select показал имя, а не голый id.
  injectedOption?: { value: string; label: string } | null;
}

function EmployeeFilter({ value, onChange, query, injectedOption }: EmployeeFilterProps) {
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);

  useEffect(() => {
    if (!query) return;
    // Без employee_id грузим обе «список-истории» и собираем уникальных сотрудников.
    const q: HistoryQuery = { date_from: query.date_from, date_to: query.date_to };
    Promise.all([fetchHistoryTimesheets(q), fetchHistoryAbsences(q)])
      .then(([weeks, absences]) => {
        const seen = new Map<number, string>();
        for (const w of weeks) {
          if (!seen.has(w.employee)) seen.set(w.employee, w.employee_name);
        }
        for (const a of absences) {
          if (!seen.has(a.employee.id)) seen.set(a.employee.id, a.employee.full_name);
        }
        const opts = Array.from(seen.entries())
          .map(([id, name]) => ({ value: String(id), label: name }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setOptions(opts);
      })
      .catch(() => {
        // молча игнорируем — основная таблица сообщит об ошибке
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.date_from, query?.date_to]);

  const data = useMemo(() => {
    if (injectedOption && !options.some((o) => o.value === injectedOption.value)) {
      return [injectedOption, ...options];
    }
    return options;
  }, [options, injectedOption]);

  return (
    <Select
      label="Сотрудник"
      placeholder="Все"
      clearable
      searchable
      data={data}
      value={value}
      onChange={onChange}
      w={260}
      nothingFoundMessage="Нет совпадений"
    />
  );
}

// ---------------------------------------------------------------------------
// Timesheet history tab
// ---------------------------------------------------------------------------

interface TimesheetHistoryTabProps {
  query: HistoryQuery | null;
}

function TimesheetHistoryTab({ query }: TimesheetHistoryTabProps) {
  const [rows, setRows] = useState<TimesheetHistoryItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<TimesheetHistoryItem | null>(null);

  useEffect(() => {
    if (!query) return;
    setLoading(true);
    setError(null);
    fetchHistoryTimesheets(query)
      .then(setRows)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.date_from, query?.date_to, query?.employee_id]);

  if (!query) {
    return <Text c="dimmed">Укажите период.</Text>;
  }
  if (loading) return <Loader size="sm" />;
  if (error) {
    return (
      <Alert color="red" title="Не удалось загрузить историю таймшитов">
        {error}
      </Alert>
    );
  }
  if (!rows || rows.length === 0) {
    return <Text c="dimmed">За указанный период нет недель.</Text>;
  }

  return (
    <>
      <ScrollArea>
        <Table withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Сотрудник</Table.Th>
              <Table.Th>Неделя</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th ta="right">Списано</Table.Th>
              <Table.Th ta="right">Норма</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.id} onClick={() => setSelected(row)} style={{ cursor: "pointer" }}>
                <Table.Td>{row.employee_name}</Table.Td>
                <Table.Td>{dayjs(row.week_start).format("DD.MM.YYYY")}</Table.Td>
                <Table.Td>
                  <StatusBadge
                    entity="timesheet"
                    code={row.status as TimesheetStatus}
                    label={row.status_display}
                  />
                </Table.Td>
                <Table.Td ta="right">{formatHours(row.allocated_hours)}</Table.Td>
                <Table.Td ta="right">{formatHours(row.required_hours)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <TimesheetHistoryDetailModal
        opened={!!selected}
        onClose={() => setSelected(null)}
        summary={selected}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Timesheet history detail modal — read-only таблица строк недели.
// ---------------------------------------------------------------------------

interface TimesheetHistoryDetailModalProps {
  opened: boolean;
  onClose: () => void;
  summary: TimesheetHistoryItem | null;
}

function TimesheetHistoryDetailModal({
  opened,
  onClose,
  summary,
}: TimesheetHistoryDetailModalProps) {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [detail, setDetail] = useState<TimesheetWeekDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened || !summary) {
      setDetail(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetchHistoryTimesheet(summary.id)
      .then(setDetail)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, summary?.id]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      fullScreen={isMobile}
      title={
        summary
          ? `${summary.employee_name} — неделя ${dayjs(summary.week_start).format("DD.MM.YYYY")}`
          : undefined
      }
    >
      {loading && <Loader size="sm" />}
      {error && (
        <Alert color="red" title="Не удалось загрузить детали">
          {error}
        </Alert>
      )}
      {detail && (
        <Stack gap="sm">
          <Group gap="sm">
            <StatusBadge
              entity="timesheet"
              code={detail.status as TimesheetStatus}
              label={detail.status_display}
            />
            <Text size="sm">
              Списано: {formatHours(detail.allocated_hours)} / {formatHours(detail.required_hours)}
            </Text>
          </Group>
          {/* US-85.1: ФИО — кто согласовал / у кого на согласовании.
              Для согласованной недели — согласанты; пока на согласовании —
              у кого «висит». У недель, согласованных до US-85, ФИО нет
              (раньше не сохранялось) → строка не выводится. */}
          {detail.status === "approved" && formatApprovedBy(detail) && (
            <Text size="sm" c="dimmed">
              Согласовано — {formatApprovedBy(detail)}
            </Text>
          )}
          {formatPendingWith(detail.pending_with) && (
            <Text size="sm" c="dimmed">
              На согласовании у: {formatPendingWith(detail.pending_with)}
            </Text>
          )}
          {detail.lr_comment && (
            <Alert color="orange" variant="light" title="Комментарий ЛР">
              {detail.lr_comment}
            </Alert>
          )}
          {detail.pm_comment && (
            <Alert color="orange" variant="light" title="Комментарий PM">
              {detail.pm_comment}
            </Alert>
          )}
          <Table withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Дата</Table.Th>
                <Table.Th>Проект</Table.Th>
                <Table.Th ta="right">Часы</Table.Th>
                <Table.Th>Jira</Table.Th>
                <Table.Th>Комментарий</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {detail.entries.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" size="sm">
                      В этой неделе нет строк.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                detail.entries.map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td>{dayjs(e.work_date).format("DD.MM.YYYY")}</Table.Td>
                    <Table.Td>
                      <Box>
                        <Text size="sm">{e.project_code}</Text>
                        <Text size="xs" c="dimmed">
                          {e.project_name}
                        </Text>
                      </Box>
                    </Table.Td>
                    <Table.Td ta="right">{formatHours(e.hours)}</Table.Td>
                    <Table.Td>{e.jira_reference || "—"}</Table.Td>
                    <Table.Td>{e.comment || "—"}</Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
          {detail.absences.length > 0 && (
            <Stack gap={4}>
              <Text fw={600} size="sm">
                Отсутствия в этой неделе:
              </Text>
              {detail.absences.map((a) => (
                <Text key={a.id} size="sm">
                  {a.absence_type_display}: {dayjs(a.start_date).format("DD.MM")}–
                  {dayjs(a.end_date).format("DD.MM")} ({a.hours_in_week} ч)
                </Text>
              ))}
            </Stack>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Закрыть
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Absence history tab
// ---------------------------------------------------------------------------

interface AbsenceHistoryTabProps {
  query: HistoryQuery | null;
}

function AbsenceHistoryTab({ query }: AbsenceHistoryTabProps) {
  const [rows, setRows] = useState<AbsenceHistoryItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AbsenceHistoryItem | null>(null);

  const load = useCallback(() => {
    if (!query) return;
    setLoading(true);
    setError(null);
    fetchHistoryAbsences(query)
      .then(setRows)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  if (!query) {
    return <Text c="dimmed">Укажите период.</Text>;
  }
  if (loading) return <Loader size="sm" />;
  if (error) {
    return (
      <Alert color="red" title="Не удалось загрузить историю отсутствий">
        {error}
      </Alert>
    );
  }
  if (!rows || rows.length === 0) {
    return <Text c="dimmed">За указанный период нет отсутствий.</Text>;
  }

  return (
    <>
      <ScrollArea>
        <Table withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Сотрудник</Table.Th>
              <Table.Th>Тип</Table.Th>
              <Table.Th>Период</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th ta="right">Дней (раб. / кал.)</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.id} onClick={() => setSelected(row)} style={{ cursor: "pointer" }}>
                <Table.Td>{row.employee.full_name}</Table.Td>
                <Table.Td>{row.absence_type_display}</Table.Td>
                <Table.Td>
                  {dayjs(row.start_date).format("DD.MM.YYYY")}
                  {row.start_date !== row.end_date &&
                    ` – ${dayjs(row.end_date).format("DD.MM.YYYY")}`}
                </Table.Td>
                <Table.Td>
                  <StatusBadge entity="absence" code={row.status} label={row.status_display} />
                </Table.Td>
                <Table.Td ta="right">
                  {row.working_days}
                  {row.calendar_days != null ? ` / ${row.calendar_days}` : ""}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <AbsenceHistoryDetailModal
        opened={!!selected}
        onClose={() => setSelected(null)}
        summary={selected}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Absence history detail modal
// ---------------------------------------------------------------------------

interface AbsenceHistoryDetailModalProps {
  opened: boolean;
  onClose: () => void;
  summary: AbsenceHistoryItem | null;
}

function AbsenceHistoryDetailModal({ opened, onClose, summary }: AbsenceHistoryDetailModalProps) {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [detail, setDetail] = useState<Absence | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened || !summary) {
      setDetail(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetchHistoryAbsence(summary.id)
      .then(setDetail)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, summary?.id]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      fullScreen={isMobile}
      title={
        summary ? `${summary.employee.full_name} — ${summary.absence_type_display}` : undefined
      }
    >
      {loading && <Loader size="sm" />}
      {error && (
        <Alert color="red" title="Не удалось загрузить детали">
          {error}
        </Alert>
      )}
      {detail && (
        <Stack gap="sm">
          <Group gap="sm">
            <StatusBadge entity="absence" code={detail.status} label={detail.status_display} />
            <Text size="sm">
              {dayjs(detail.start_date).format("DD.MM.YYYY")} –{" "}
              {dayjs(detail.end_date).format("DD.MM.YYYY")} ({detail.working_days} раб.
              {detail.calendar_days != null ? ` / ${detail.calendar_days} кал.` : ""} дн.)
            </Text>
          </Group>
          {/* US-88: ФИО согласантов (ЛР / HR) — кто фактически одобрил заявку. */}
          {(detail.lr_approved_by_name || detail.hr_approved_by_name) && (
            <Text size="sm" c="dimmed">
              Согласовал
              {detail.lr_approved_by_name ? ` ЛР: ${detail.lr_approved_by_name}` : ""}
              {detail.lr_approved_by_name && detail.hr_approved_by_name ? " · " : ""}
              {detail.hr_approved_by_name ? ` HR: ${detail.hr_approved_by_name}` : ""}
            </Text>
          )}
          {detail.comment && (
            <Box>
              <Text fw={600} size="sm">
                Комментарий сотрудника
              </Text>
              <Text size="sm">{detail.comment}</Text>
            </Box>
          )}
          {detail.lr_comment && (
            <Alert color="orange" variant="light" title="Комментарий ЛР">
              {detail.lr_comment}
            </Alert>
          )}
          {detail.hr_comment && (
            <Alert color="orange" variant="light" title="Комментарий HR">
              {detail.hr_comment}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Закрыть
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
